import { HarnessFrame } from '@flowx/designer/modules/ai-builder/types/harness.types'
import { asString, isRecord } from '@flowx/designer/modules/ai-builder/utils/payload.util'

/**
 * What the supervisor reached for during one turn, for the diagram on the hold.
 *
 * Built only from frames the stream already carries: the supervisor's own tool calls (subagent
 * internals are filtered out by the harness before they reach the wire) and the end-of-turn
 * `usage`. So a lane lights up when the supervisor calls into it, not while a subagent works inside
 * it, and the token count is an estimate until `usage` lands.
 */

/** Where a call goes. `subagent` is a `task` delegation; the rest follow the tool's prefix. */
export type HarnessTurnLane = 'platform' | 'subagent' | 'planner' | 'maker'

export type HarnessTurnCall = {
  toolUseId: string
  /** The raw tool name, e.g. `read_resource` or `task`. */
  tool: string
  /** The harness's human label, else the raw name. Never the arguments themselves. */
  label: string
  lane: HarnessTurnLane
  /** The subagent a `task` was handed to, e.g. `inspector`. Empty for every other call. */
  agent: string
  status: 'running' | 'done' | 'failed'
  startedAt: number
  endedAt?: number
}

export type HarnessTurnActivity = {
  startedAt: number
  calls: readonly HarnessTurnCall[]
  /** Characters the turn has streamed and passed through tools. A quarter of it is the live token
   * estimate — the same rule of thumb the harness's own budget charges by. */
  chars: number
  /** The harness's charge for the turn, which it reports once, just before `end`. */
  usage: { estimatedTokens: number; remainingToday: number } | null
}

export function startTurnActivity(at: number): HarnessTurnActivity {
  return { startedAt: at, calls: [], chars: 0, usage: null }
}

export function foldTurnActivity(
  turn: HarnessTurnActivity | null,
  frame: HarnessFrame,
  at: number
): HarnessTurnActivity | null {
  if (!turn) {
    return turn
  }

  switch (frame.type) {
    case 'token':
      return { ...turn, chars: turn.chars + frame.text.length }

    case 'tool_use_start':
      return {
        ...turn,
        chars: turn.chars + sizeOf(frame.arguments),
        calls: [
          ...turn.calls,
          {
            toolUseId: frame.toolUseId,
            tool: frame.name,
            label: frame.label || frame.name,
            lane: laneOf(frame.name),
            agent: frame.name === 'task' ? subagentOf(frame.arguments) : '',
            status: 'running',
            startedAt: at,
          },
        ],
      }

    case 'tool_use_result': {
      const index = turn.calls.findIndex(
        (call) => call.toolUseId === frame.toolUseId && call.status === 'running'
      )

      if (index === -1) {
        return { ...turn, chars: turn.chars + sizeOf(frame.content) }
      }

      const status = failed(frame.content) ? 'failed' : 'done'

      return {
        ...turn,
        chars: turn.chars + sizeOf(frame.content),
        calls: turn.calls.map((call, i) => (i === index ? { ...call, status, endedAt: at } : call)),
      }
    }

    case 'usage':
      return {
        ...turn,
        usage: { estimatedTokens: frame.estimatedTokens, remainingToday: frame.remainingToday },
      }

    default:
      return turn
  }
}

export function laneOf(tool: string): HarnessTurnLane {
  if (tool === 'task') {
    return 'subagent'
  }

  if (tool === 'ask_user') {
    return 'maker'
  }

  return tool.startsWith('plan_') ? 'planner' : 'platform'
}

/** `general-purpose` reads as noise on a diagram; the rest are the harness's own role names. */
function subagentOf(args: unknown): string {
  const type = isRecord(args) ? asString(args['subagent_type']) : ''

  return type === 'general-purpose' ? 'helper' : type.replace(/[_-]+/g, ' ')
}

/** The transcript row's rule, so the diagram and the trace never disagree: a truthy `error` or a
 * `recoverable` flag. A key named `error` holding null is a field, not a failure. */
function failed(content: unknown): boolean {
  let parsed = content

  if (typeof content === 'string') {
    try {
      parsed = JSON.parse(content)
    } catch {
      return false
    }
  }

  return isRecord(parsed) && (!!parsed['error'] || parsed['recoverable'] === true)
}

function sizeOf(value: unknown): number {
  if (value == null) {
    return 0
  }

  if (typeof value === 'string') {
    return value.length
  }

  try {
    return JSON.stringify(value).length
  } catch {
    return 0
  }
}
